/**
 * Pure utility functions for ISO 8601 date manipulation.
 *
 * All dates are represented as `YYYY-MM-DD` strings (ISO 8601 calendar date).
 * No `Date` object is exposed in the public API — parsing/formatting
 * happens internally to avoid timezone ambiguity.
 */
// ── Parsing / Formatting ──────────────────────────────────────────────────────
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Parse an ISO `YYYY-MM-DD` string. Returns `null` on invalid input. */
export function parseIsoDate(value) {
    if (!value)
        return null;
    const m = ISO_DATE_RE.exec(value);
    if (!m)
        return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
        return null;
    }
    return { year, month, day };
}
/** Format a CalendarDate as `YYYY-MM-DD`. */
export function formatIsoDate(d) {
    const y = String(d.year).padStart(4, "0");
    const m = String(d.month).padStart(2, "0");
    const day = String(d.day).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
/** Get today's date as a CalendarDate. */
export function today() {
    const now = new Date();
    return {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        day: now.getDate(),
    };
}
// ── Date Arithmetic ───────────────────────────────────────────────────────────
/** Number of days in a given month (1-based). */
export function daysInMonth(year, month) {
    // Day 0 of next month = last day of current month
    return new Date(year, month, 0).getDate();
}
/** Day of week for the 1st of the month (0 = Sunday, 6 = Saturday). */
export function firstWeekday(year, month) {
    return new Date(year, month - 1, 1).getDay();
}
/** Check if two CalendarDates represent the same day. */
export function isSameDay(a, b) {
    return a.year === b.year && a.month === b.month && a.day === b.day;
}
/** Compare two CalendarDates. Returns <0, 0, or >0. */
export function compareDates(a, b) {
    return a.year - b.year || a.month - b.month || a.day - b.day;
}
/** Check if a date falls within [min, max] (inclusive). Null bounds are ignored. */
export function isDateInRange(date, min, max) {
    if (min && compareDates(date, min) < 0)
        return false;
    if (max && compareDates(date, max) > 0)
        return false;
    return true;
}
/**
 * Whether a whole month lies outside the range, so a month picker can grey it out.
 *
 * The question a calendar asks when it offers months rather than days, and asking it of a *date*
 * gets it wrong at the edges: the first of a month can be before `min` while most of that month is
 * reachable. So it is asked of the month.
 */
export function isMonthOutOfRange(year, month, min, max) {
    if (min && (year < min.year || (year === min.year && month < min.month)))
        return true;
    if (max && (year > max.year || (year === max.year && month > max.month)))
        return true;
    return false;
}
/** Whether a whole year lies outside the range, for the same reason as the month above. */
export function isYearOutOfRange(year, min, max) {
    return (min !== null && year < min.year) || (max !== null && year > max.year);
}
/**
 * The years a year picker offers.
 *
 * A bound is a bound: a field that accepts 2020 to 2030 offers eleven years, not two centuries with
 * eleven of them enabled. Where there is no bound the span is wide enough that a birth date and a
 * far maturity are both reachable by scrolling.
 *
 * Always contains the year on screen, bound or not — a picker that cannot show where it already is
 * has no way back, and a view can sit outside the bounds when a value arrives from elsewhere.
 *
 * Stated once because two renderers each choosing a span is two different pickers.
 */
export function calendarYearRange(viewYear, min, max) {
    const start = Math.min(min?.year ?? Math.min(viewYear - 100, 1920), viewYear);
    const end = Math.max(max?.year ?? Math.max(viewYear + 100, 2120), viewYear);
    const years = [];
    for (let year = start; year <= end; year += 1)
        years.push(year);
    return years;
}
/** Add months to a CalendarDate, clamping the day if needed. */
export function addMonths(d, count) {
    let month = d.month + count;
    let year = d.year;
    // Normalize month to 1–12
    year += Math.floor((month - 1) / 12);
    month = ((((month - 1) % 12) + 12) % 12) + 1;
    const maxDay = daysInMonth(year, month);
    return { year, month, day: Math.min(d.day, maxDay) };
}
/** Add years to a CalendarDate, clamping Feb 29 if needed. */
export function addYears(d, count) {
    const year = d.year + count;
    const maxDay = daysInMonth(year, d.month);
    return { year, month: d.month, day: Math.min(d.day, maxDay) };
}
/** Add days, returning a new CalendarDate. */
export function addDays(d, count) {
    const dt = new Date(d.year, d.month - 1, d.day + count);
    return {
        year: dt.getFullYear(),
        month: dt.getMonth() + 1,
        day: dt.getDate(),
    };
}
// ── Grid Generation ───────────────────────────────────────────────────────────
/**
 * Build a 6×7 calendar grid for the given month.
 *
 * @param year           Full year (e.g. 2026)
 * @param month          1-based month (1 = January)
 * @param firstDayOfWeek 0 = Sunday, 1 = Monday, …
 * @returns              42 CalendarCells (6 rows × 7 columns)
 */
export function buildMonthGrid(year, month, firstDayOfWeek) {
    const totalDays = daysInMonth(year, month);
    const startWeekday = firstWeekday(year, month);
    // How many leading cells from the previous month
    const leadingBlanks = (startWeekday - firstDayOfWeek + 7) % 7;
    const cells = [];
    // Previous month fill
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    const prevDays = daysInMonth(prevYear, prevMonth);
    for (let i = leadingBlanks - 1; i >= 0; i--) {
        const day = prevDays - i;
        const date = { year: prevYear, month: prevMonth, day };
        cells.push({ date, iso: formatIsoDate(date), inMonth: false });
    }
    // Current month
    for (let d = 1; d <= totalDays; d++) {
        const date = { year, month, day: d };
        cells.push({ date, iso: formatIsoDate(date), inMonth: true });
    }
    // Next month fill (pad to 42)
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    let nextDay = 1;
    while (cells.length < 42) {
        const date = {
            year: nextYear,
            month: nextMonth,
            day: nextDay++,
        };
        cells.push({ date, iso: formatIsoDate(date), inMonth: false });
    }
    return cells;
}
// ── Range Helpers ─────────────────────────────────────────────────────────────
/**
 * Check if a date falls strictly between two dates (exclusive of endpoints).
 * Returns `false` when either bound is `null`.
 */
export function isDateBetween(date, start, end) {
    if (!start || !end)
        return false;
    return compareDates(date, start) > 0 && compareDates(date, end) < 0;
}
/**
 * Normalise a pair of dates so that `start <= end`.
 * Returns `[earlier, later]`. If either is `null` the pair is returned as-is.
 */
export function orderDates(a, b) {
    if (!a || !b)
        return [a, b];
    return compareDates(a, b) <= 0 ? [a, b] : [b, a];
}
// ─── Localized typing ─────────────────────────────────────────────────────────
/**
 * Detects the day/month/year order for a BCP 47 locale from
 * `Intl.DateTimeFormat.formatToParts` (e.g. it-IT → d/m/y, en-US → m/d/y).
 * Falls back to d/m/y when Intl is unavailable.
 */
export function localeDateOrder(locale) {
    try {
        const parts = new Intl.DateTimeFormat(locale, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).formatToParts(new Date(2001, 1, 3));
        const order = parts
            .map(p => p.type)
            .filter((t) => t === "day" || t === "month" || t === "year");
        if (order.length === 3)
            return order;
    }
    catch {
        // fall through to the ISO-ish default
    }
    return ["day", "month", "year"];
}
/**
 * Parses a date typed in the locale's numeric format (`31/12/2026`,
 * `12/31/2026`, `31.12.2026`, …). ISO `YYYY-MM-DD` is always accepted.
 * Two-digit years map to 2000-2099. Returns `null` on anything invalid.
 */
export function parseLocalizedDate(raw, locale) {
    if (!raw)
        return null;
    const iso = parseIsoDate(raw.trim());
    if (iso)
        return iso;
    const numbers = raw.trim().split(/[^0-9]+/).filter(s => s.length > 0);
    if (numbers.length !== 3)
        return null;
    // A 4-digit token is the year regardless of position (e.g. "2026/12/31").
    const order = localeDateOrder(locale);
    const values = {};
    const fourDigitIndex = numbers.findIndex(n => n.length === 4);
    if (fourDigitIndex >= 0 && order[fourDigitIndex] !== "year") {
        // Year-first input ("2026/12/31") reads big-endian: month before day.
        const remaining = fourDigitIndex === 0
            ? ["month", "day"]
            : order.filter((part) => part !== "year");
        let cursor = 0;
        numbers.forEach((n, i) => {
            if (i === fourDigitIndex)
                values.year = Number(n);
            else {
                const part = remaining[cursor++];
                if (part)
                    values[part] = Number(n);
            }
        });
    }
    else {
        order.forEach((part, i) => {
            values[part] = Number(numbers[i]);
        });
    }
    let { year } = values;
    const { month, day } = values;
    if (year === undefined || month === undefined || day === undefined) {
        return null;
    }
    if (year < 100)
        year += 2000;
    if (year < 1000 ||
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > daysInMonth(year, month)) {
        return null;
    }
    return { year, month, day };
}
/**
 * The text a person reads for a date: the month named, in the reader's language and order.
 * `2026-04-03` reads "3 April 2026", "3 aprile 2026", "3. April 2026".
 *
 * A named month cannot be transposed — `April` is not a number — so the display carries no
 * ambiguity about which of two numbers is the month. This is the inverse of
 * `parseLocalizedDate`, which accepts the numeric orders a person may type; what is typed is
 * wide, what is shown is unambiguous, and the value behind both stays ISO.
 *
 * Returns the ISO text unchanged when the value cannot be read as a date, so a field showing
 * something a person typed does not blank itself while they are still typing.
 */
export function formatLocalizedDate(value, locale) {
    if (value === null || value === undefined)
        return "";
    const date = typeof value === "string" ? parseIsoDate(value.trim().substring(0, 10)) : value;
    if (!date)
        return typeof value === "string" ? value : "";
    try {
        return new Intl.DateTimeFormat(locale, {
            year: "numeric",
            month: "long",
            day: "numeric",
        }).format(new Date(date.year, date.month - 1, date.day));
    }
    catch {
        return formatIsoDate(date);
    }
}
